<script lang="ts" setup>
import { inject } from 'vue'
import { useChapterTheme } from '../composables/useChapterTheme'
import type { ChapterConfig } from '@/composables/payment'
import { theme as brandTheme } from '@/config/brands'

interface Props {
  value: boolean
  chapter: any
}

defineProps<Props>()
const emit = defineEmits<{
  (e: 'input', value: boolean): void
  (e: 'prev-chapter'): void
  (e: 'next-chapter'): void
  (e: 'open-toc'): void
}>()

const config = inject<ChapterConfig>('chapterConfig')!
const { theme, THEME_BACKGROUNDS, THEME_SKINS, onChangeThemeSize, setSkin } = useChapterTheme()

function handlePrev() {
  emit('prev-chapter')
}

function handleNext() {
  emit('next-chapter')
}

function handleOpenTOC() {
  emit('open-toc')
}
</script>

<template>
  <BasicPopup :value="value" :mask="false" @input="emit('input', $event)">
    <div class="rr-theme-popup-panel w-full px-5 pt-25px pb-10 bg-n-bg-card" :style="`background: ${brandTheme.bgCard || '#1D232D'}`">
      <!-- 字体大小调节 -->
      <div class="mb-5 flex gap-4 items-center justify-between">
        <p class="text-14px text-n-text-2 whitespace-nowrap">
          {{ config.texts.themeSizeLabel }}
        </p>
        <div class="rr-font-size-control flex-1 flex items-center justify-between">
          <span
            class="w-full h-35px lh-35px text-center text-14px bg-n-bg-tag b-rd-tl-18px b-rd-bl-18px"
            :class="{ 'opacity-40': theme.size === '24' }" @click="onChangeThemeSize(-1)"
          >
            {{ config.texts.fontSizeDecrease }}
          </span>
          <span class="flex justify-between items-center w-full h-35px lh-35px min-w-5 text-15px text-center bg-n-bg-tag">
            <span class="rr-size-divider">|</span>
            <span>{{ theme.size }}</span>
            <span class="rr-size-divider">|</span>
          </span>
          <span
            class="w-full h-35px lh-35px text-center text-14px bg-n-bg-tag b-rd-tr-18px b-rd-br-18px"
            :class="{ 'opacity-40': theme.size === '50' }" @click="onChangeThemeSize(1)"
          >
            {{ config.texts.fontSizeIncrease }}
          </span>
        </div>
      </div>

      <!-- 主题颜色选择 -->
      <div class="flex items-center">
        <div class="flex flex-1 items-center justify-center space-x-12px">
          <!-- 皮肤主题 -->
          <span
            v-for="skin in THEME_SKINS"
            :key="skin.id"
            class="w-30px h-30px border rounded-full overflow-hidden flex-shrink-0 transition-all duration-200"
            :class="skin.id === theme.skinId ? 'border-2 border-n-primary scale-110' : 'border-n-border-primary'"
            @click="setSkin(skin.id)"
          >
            <img :src="skin.thumb" class="w-full h-full object-cover">
          </span>
          <!-- 背景颜色 -->
          <span
            v-for="item in THEME_BACKGROUNDS" :key="item" class="w-30px h-30px border rounded-full flex-shrink-0 transition-all duration-200"
            :style="`background: ${item};`"
            :class="item === theme.background && !theme.skinId ? 'border-2 border-n-primary scale-110' : 'border-n-border-primary'"
            @click="theme.background = item; theme.skinId = ''"
          />
        </div>
      </div>

      <!-- 导航按钮 -->
      <div class="mt-5 flex items-center justify-between">
        <span
          class="theme-btn-bg rr-theme-action w-100px h-32px lh-32px text-center text-14px bg-n-bg-tag rounded-full"
          :class="{ 'opacity-40': !chapter?.prevId }" @click="handlePrev"
        >
          {{ config.texts.prevButton }}
        </span>
        <span
          class="theme-btn-bg rr-theme-action w-100px h-32px lh-32px text-center text-14px bg-n-bg-tag rounded-full"
          @click.stop="handleOpenTOC"
        >
          {{ config.texts.catalogueButton }}
        </span>
        <span
          class="theme-btn-bg rr-theme-action w-100px h-32px lh-32px text-center text-14px bg-n-bg-tag rounded-full"
          :class="{ 'opacity-40': !chapter?.nextId }"
          @click="handleNext"
        >
          {{ config.texts.nextButton }}
        </span>
      </div>
    </div>
  </BasicPopup>
</template>

<style scoped>
.rr-theme-popup-panel {
  border-radius: 18px 18px 0 0 !important;
}

.rr-size-divider {
  color: var(--rr-text-strong);
  opacity: 0.1;
}

.rr-theme-action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px !important;
  overflow: hidden;
  color: #FFFFFF !important;
  -webkit-text-fill-color: #FFFFFF !important;
}
</style>
